import React, { useEffect, useState } from "react";
import { Row, Col, Typography, Button, Spin, Tag, Divider, message } from "antd";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, EditOutlined, DeleteOutlined, TagOutlined, CheckCircleOutlined, ClockCircleOutlined, InfoCircleOutlined, WarningOutlined, StopOutlined } from "@ant-design/icons";
import CouponServ from "../../../service/CouponServ";
import { formatDate } from "../../../util/dateUtils";
import { usePreference } from "../../../context/PreferenceContext";

const { Title, Text } = Typography;

const CouponDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { formatPrice } = usePreference();
    
    const [coupon, setCoupon] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchCoupon = async () => {
        try {
            const res = await CouponServ.single(id);
            setCoupon(res.data || res);
        } catch (error) {
            message.error("Failed to load coupon details.");
            navigate("/admin/coupons/list");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCoupon();
    }, [id]);

    const handleDelete = async () => {
        try {
            await CouponServ.remove(id);
            message.success("Coupon deleted.");
            navigate("/admin/coupons/list");
        } catch (error) {
            message.error("Failed to delete coupon.");
        }
    };

    if (loading) return <div style={{ textAlign: 'center', padding: '100px 0' }}><Spin size="large" /></div>;
    if (!coupon) return null;

    // Compute effective display status from data
    const getEffectiveStatus = (c) => {
        if (!c) return { label: 'INACTIVE', color: 'default', icon: null };
        const now = new Date();
        const isExpired = c.endDate && new Date(c.endDate) < now;
        const isLimitReached = c.usageLimit && c.usedCount >= c.usageLimit;
        if (isExpired)     return { label: 'EXPIRED',       color: 'orange',  icon: <WarningOutlined /> };
        if (isLimitReached) return { label: 'LIMIT REACHED', color: 'red',     icon: <StopOutlined /> };
        if (c.isActive)    return { label: 'ACTIVE',        color: 'success', icon: <CheckCircleOutlined /> };
        return               { label: 'INACTIVE',        color: 'default', icon: null };
    };
    const effectiveStatus = getEffectiveStatus(coupon);

    return (
        <div style={{ background: '#fff', padding: '0', borderRadius: '12px', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
            <div style={{ padding: '24px', background: 'linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', gap: '16px' }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/admin/coupons/list")} style={{ marginTop: 4 }} />
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: 8 }}>
                            <Title level={4} style={{ margin: 0, fontWeight: 900, color: '#1e293b' }}>{coupon.code}</Title>
                            <Tag color={effectiveStatus.color} icon={effectiveStatus.icon} style={{ fontWeight: 700 }}>
                                {effectiveStatus.label}
                            </Tag>
                            <Tag color="blue">{coupon.couponType?.toUpperCase() || 'GENERAL'}</Tag>
                            {coupon.freeDelivery && <Tag color="cyan">FREE DELIVERY</Tag>}
                        </div>
                        <Text type="secondary" style={{ fontSize: 13, fontWeight: 500 }}>{coupon.description || "No description provided."}</Text>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <Button icon={<EditOutlined />} onClick={() => navigate(`/admin/coupons/edit/${id}`)}>Edit</Button>
                    <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>Delete</Button>
                </div>
            </div>

            <div style={{ padding: '24px' }}>
                <Row gutter={[24, 24]}>
                    <Col xs={24} md={16}>
                        <div style={{ marginBottom: 32 }}>
                            <Title level={5} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569' }}>
                                <TagOutlined /> Discount Configuration
                            </Title>
                            <div style={{ background: '#f8fafc', padding: 16, borderRadius: 8, border: '1px solid #f1f5f9', display: 'flex', gap: 48 }}>
                                <div>
                                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Value</div>
                                    <div style={{ fontSize: 20, fontWeight: 800, color: '#1e293b' }}>
                                        {coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : formatPrice(coupon.discountValue)}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Max Discount</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginTop: 4 }}>
                                        {coupon.maxDiscountAmount ? formatPrice(coupon.maxDiscountAmount) : 'Unlimited'}
                                    </div>
                                </div>
                                <div>
                                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Min Order Required</div>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', marginTop: 4 }}>
                                        {formatPrice(coupon.minOrderAmount)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div>
                            <Title level={5} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569' }}>
                                <ClockCircleOutlined /> Validity & Limits
                            </Title>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                            <span style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Start Date</span>
                                            <span style={{ color: '#1e293b', fontSize: 13, fontWeight: 700 }}>{formatDate(coupon.startDate)}</span>
                                        </div>
                                        <Divider style={{ margin: '8px 0' }} />
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>End Date</span>
                                            <span style={{ color: '#1e293b', fontSize: 13, fontWeight: 700 }}>{formatDate(coupon.endDate)}</span>
                                        </div>
                                    </div>
                                </Col>
                                <Col span={12}>
                                    <div style={{ padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                            <span style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Global Limit</span>
                                            <span style={{ color: '#1e293b', fontSize: 13, fontWeight: 700 }}>{coupon.usageLimit || 'Unlimited'}</span>
                                        </div>
                                        <Divider style={{ margin: '8px 0' }} />
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <span style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Per-User Limit</span>
                                            <span style={{ color: '#1e293b', fontSize: 13, fontWeight: 700 }}>{coupon.userUsageLimit || 1} times</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Col>

                    <Col xs={24} md={8}>
                        <div style={{ background: '#f8fafc', padding: 24, borderRadius: 12, border: '1px solid #e2e8f0', height: '100%' }}>
                            <Title level={5} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', marginTop: 0 }}>
                                <InfoCircleOutlined /> Performance
                            </Title>
                            
                            <div style={{ marginTop: 24, textAlign: 'center' }}>
                                <div style={{ fontSize: 48, fontWeight: 900, color: '#3b82f6', lineHeight: 1 }}>{coupon.usedCount || 0}</div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginTop: 8, letterSpacing: 1 }}>Total Redemptions</div>
                            </div>

                            <Divider />

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Created At</Text>
                                    <Text strong>{formatDate(coupon.createdAt)}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Last Updated</Text>
                                    <Text strong>{formatDate(coupon.updatedAt)}</Text>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Text type="secondary">Visibility</Text>
                                    <Text strong>{coupon.isPublic ? 'Public' : 'Hidden'}</Text>
                                </div>
                            </div>

                            <Button type="primary" block style={{ marginTop: 24 }} onClick={() => navigate('/admin/coupons/usage')}>
                                View Usage Logs
                            </Button>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default CouponDetails;
